import React, { useState, useRef, useEffect, useLayoutEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';
import type { TooltipContent } from '../../types/form.types';

/** Minimum left edge (px) for trigger; when trigger is left of this, tooltip is left-aligned to avoid going behind sidebar. */
const TOOLTIP_SAFE_LEFT_EDGE_PX = 320;

interface TooltipProps {
  text?: string; // Legacy: simple string tooltip
  content?: TooltipContent; // Structured tooltip content
  className?: string;
  children?: React.ReactNode;
  /** 'light' = light background (e.g. for Approve/Reject) so it doesn't merge with dark question tooltips. */
  variant?: 'default' | 'light';
  /** When true, left-align tooltip to trigger when trigger is near left edge so tooltip doesn't go behind sidebar. */
  preferLeftAlignWhenNearEdge?: boolean;
  /**
   * When true, tooltip width follows content (capped by max-w-tooltip) instead of min-w-tooltip.
   * Use for short labels (e.g. status hints); form help text should keep the default min width.
   */
  fitContent?: boolean;
  /**
   * When false, the wrapper is not a tab stop (`tabIndex={-1}`). Use when children are already
   * focusable (buttons, links, dialog triggers) so keyboard users get a single tab stop.
   * Focus/blur still bubble from the child for hover/focus tooltips. Default true.
   */
  focusableWrapper?: boolean;
}

// Global tooltip manager to ensure only one tooltip is visible at a time
class TooltipManager {
  private activeTooltip: (() => void) | null = null;

  register(dismissCallback: () => void): void {
    if (!dismissCallback) return; // Guard against null/undefined
    
    // Dismiss any previously active tooltip
    if (this.activeTooltip) {
      const previous = this.activeTooltip;
      this.activeTooltip = null; // Clear before calling to prevent re-registration issues
      previous(); // This will hide the previous tooltip and unregister it
    }
    // Register this tooltip as the active one
    this.activeTooltip = dismissCallback;
  }

  unregister(dismissCallback: () => void): void {
    // Only unregister if this is the currently active tooltip
    if (this.activeTooltip === dismissCallback) {
      this.activeTooltip = null;
    }
  }

  /**
   * Check if a given callback is the currently active tooltip
   * Useful for race condition prevention
   */
  isActive(dismissCallback: () => void): boolean {
    return this.activeTooltip === dismissCallback;
  }

  /**
   * Clear all tooltips (useful for testing or cleanup)
   */
  clearAll(): void {
    if (this.activeTooltip) {
      const previous = this.activeTooltip;
      this.activeTooltip = null;
      previous();
    }
  }
}

const tooltipManager = new TooltipManager();

export const Tooltip: React.FC<TooltipProps> = ({
  text,
  content,
  className = '',
  children,
  variant = 'default',
  preferLeftAlignWhenNearEdge = false,
  fitContent = false,
  focusableWrapper = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const [isPositioned, setIsPositioned] = useState(false); // Track if position is calculated
  const [useLeftAlign, setUseLeftAlign] = useState(false); // When true, align tooltip left edge to trigger (avoid sidebar)
  /** When light variant, tooltip is portaled to body; this is its fixed position so it draws above the Sections panel. */
  const [portalPosition, setPortalPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const positionCalculatedRef = useRef<'top' | 'bottom' | 'left' | 'right' | null>(null); // Track calculated position
  
  // Create a stable dismiss function reference for the manager
  const dismissTooltip = useCallback(() => {
    // Clear any pending show timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    // Unregister from manager when dismissed
    tooltipManager.unregister(dismissTooltip);
    // Only update state if component is still mounted
    if (isMountedRef.current) {
      setIsVisible(false);
    }
  }, []);

  const handleMouseEnter = () => {
    if (!isMountedRef.current) return; // Guard against calls after unmount
    
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      // Check if component is still mounted and this tooltip should be shown
      if (!isMountedRef.current) return;
      
      // Register with manager before showing - this will dismiss any other active tooltip
      tooltipManager.register(dismissTooltip);
      // Double-check we're still the active tooltip (race condition protection)
      if (tooltipManager.isActive(dismissTooltip) && isMountedRef.current) {
        setIsVisible(true);
      }
    }, 200);
  };

  const handleMouseLeave = () => {
    if (!isMountedRef.current) return; // Guard against calls after unmount
    
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      // Unregister from manager when hiding
      tooltipManager.unregister(dismissTooltip);
      setIsVisible(false);
    }, 100);
  };

  const handleFocus = () => {
    if (!isMountedRef.current) return; // Guard against calls after unmount
    
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    showTimeoutRef.current = setTimeout(() => {
      // Check if component is still mounted and this tooltip should be shown
      if (!isMountedRef.current) return;
      
      // Register with manager before showing - this will dismiss any other active tooltip
      tooltipManager.register(dismissTooltip);
      // Double-check we're still the active tooltip (race condition protection)
      if (tooltipManager.isActive(dismissTooltip) && isMountedRef.current) {
        setIsVisible(true);
      }
    }, 200);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!isMountedRef.current) return; // Guard against calls after unmount
    
    // Don't hide if focus is moving to tooltip or within children
    // This prevents premature tooltip dismissal when focus moves within the component tree
    const relatedTarget = e.relatedTarget as Node | null;
    if (
      relatedTarget &&
      (tooltipRef.current?.contains(relatedTarget) ||
        triggerRef.current?.contains(relatedTarget))
    ) {
      return;
    }

    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    hideTimeoutRef.current = setTimeout(() => {
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      // Unregister from manager when hiding
      tooltipManager.unregister(dismissTooltip);
      setIsVisible(false);
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isVisible && isMountedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      // Unregister from manager when hiding
      tooltipManager.unregister(dismissTooltip);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsVisible(false);
      }
      // Return focus to trigger if it's focusable
      // Use requestAnimationFrame to ensure focus happens after state update
      requestAnimationFrame(() => {
        if (triggerRef.current && 'focus' in triggerRef.current && isMountedRef.current) {
          (triggerRef.current as HTMLElement).focus();
        }
      });
    }
  };

  // Track mount state and cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false; // Mark as unmounted first
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      // Clean up: unregister from manager when component unmounts
      tooltipManager.unregister(dismissTooltip);
    };
  }, [dismissTooltip]);

  // Calculate position based on available space in all four directions
  // Memoized with useCallback to avoid recreation on every render
  // Returns the calculated position without setting state (for synchronous use)
  const calculatePosition = useCallback((): 'top' | 'bottom' | 'left' | 'right' | null => {
    // Guard against calls after unmount (following React best practices for async operations)
    if (!isMountedRef.current) return null;
    
    if (tooltipRef.current && triggerRef.current) {
      const tooltip = tooltipRef.current;
      const trigger = triggerRef.current;
      
      const tooltipRect = tooltip.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      
      // Gap between tooltip and trigger (8px as per Figma spec)
      const gap = 8;
      
      // Always use top or bottom positioning (centered) to avoid jumping issues
      // Never use left/right positioning as it causes layout shifts
      let newPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
      
      // Try top first (preferred)
      if (triggerRect.top - tooltipRect.height - gap >= 0) {
        newPosition = 'top';
      }
      // Try bottom if top doesn't fit
      else if (triggerRect.bottom + tooltipRect.height + gap <= viewport.height) {
        newPosition = 'bottom';
      }
      // If neither top nor bottom fits, use whichever has more space
      else {
        const spaceAbove = triggerRect.top;
        const spaceBelow = viewport.height - triggerRect.bottom;
        newPosition = spaceAbove > spaceBelow ? 'top' : 'bottom';
      }
      
      return newPosition;
    }
    return null;
  }, []); // Empty deps - only uses refs which are stable

  // Following React best practices: measure synchronously before browser paint
  // For large content, force multiple layout passes to ensure dimensions are accurate
  // Calculate position and set it in the same render cycle to prevent jumps
  useLayoutEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      // Only process if not already positioned to avoid unnecessary work
      if (isPositioned) return;
      
      const tooltip = tooltipRef.current;
      const trigger = triggerRef.current;
      
      // Force multiple synchronous layout passes for large content
      // Some browsers need multiple passes to fully calculate dimensions for complex content
      // Access layout properties in sequence to force complete layout calculation
      
      // First pass: force initial layout
      void tooltip.offsetHeight;
      void tooltip.scrollHeight;
      void tooltip.offsetWidth;
      void tooltip.scrollWidth;
      void trigger.offsetHeight;
      void trigger.offsetWidth;
      
      // Second pass: force layout recalculation
      void tooltip.offsetHeight; // Force another layout pass
      void tooltip.scrollHeight; // Force layout for text content
      
      // Third pass: final measurement after all layout is forced
      void tooltip.getBoundingClientRect(); // Force final layout calculation
      const finalRect = tooltip.getBoundingClientRect();
      
      // Only proceed if we have valid dimensions
      if (finalRect.width > 0 && finalRect.height > 0) {
        const triggerRect = trigger.getBoundingClientRect();
        const leftAlign = preferLeftAlignWhenNearEdge && triggerRect.left < TOOLTIP_SAFE_LEFT_EDGE_PX;
        setUseLeftAlign(leftAlign);

        // Calculate position synchronously (returns position without setting state)
        const newPosition = calculatePosition();
        
        if (newPosition && isMountedRef.current) {
          // Store calculated position
          positionCalculatedRef.current = newPosition;
          setPosition(newPosition);
          
          // Force layout recalculation with new position classes (non-portal) or for measuring (portal)
          void tooltip.offsetHeight;
          void tooltip.getBoundingClientRect();
          setIsPositioned(true);

          // Compute fixed position for portaled tooltip so it draws above all form elements (sections, datepicker, etc.)
          const gap = 8;
          const viewportPad = 8;
          let top = 0;
          let left = 0;
          if (newPosition === 'top') {
            top = triggerRect.top - finalRect.height - gap;
          } else {
            top = triggerRect.bottom + gap;
          }
          if (leftAlign) {
            left = triggerRect.left;
          } else {
            left = triggerRect.left + triggerRect.width / 2 - finalRect.width / 2;
          }
          left = Math.max(viewportPad, Math.min(left, window.innerWidth - finalRect.width - viewportPad));
          top = Math.max(viewportPad, Math.min(top, window.innerHeight - finalRect.height - viewportPad));
          setPortalPosition({ top, left });
        }
      }
    } else if (!isVisible) {
      setIsPositioned(false);
      positionCalculatedRef.current = null;
      setUseLeftAlign(false);
      setPortalPosition(null);
    }
  }, [isVisible, calculatePosition, preferLeftAlignWhenNearEdge, variant]);
  
  // Verify position is correct after it's been set
  // This ensures tooltip doesn't jump when position changes from default to calculated
  useLayoutEffect(() => {
    if (isVisible && isPositioned && positionCalculatedRef.current && 
        position === positionCalculatedRef.current && 
        tooltipRef.current && triggerRef.current) {
      // Position is correct and tooltip is positioned - no action needed
      // This effect just ensures we don't show the tooltip until position matches calculated
    }
  }, [isVisible, isPositioned, position]);

  // Recalculate position on scroll and resize when tooltip is visible
  useEffect(() => {
    if (!isVisible) return;

    const updatePosition = () => {
      if (!isMountedRef.current || !tooltipRef.current || !triggerRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const gap = 8;
      const viewportPad = 8;
      // Recompute fixed position for portaled tooltip (both variants) on scroll/resize
      const newPosition = calculatePosition();
      if (!newPosition) return;
      const leftAlign = preferLeftAlignWhenNearEdge && triggerRect.left < TOOLTIP_SAFE_LEFT_EDGE_PX;
      let top = 0;
      let left = 0;
      if (newPosition === 'top') {
        top = triggerRect.top - tooltipRect.height - gap;
      } else {
        top = triggerRect.bottom + gap;
      }
      if (leftAlign) {
        left = triggerRect.left;
      } else {
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      }
      left = Math.max(viewportPad, Math.min(left, window.innerWidth - tooltipRect.width - viewportPad));
      top = Math.max(viewportPad, Math.min(top, window.innerHeight - tooltipRect.height - viewportPad));
      setPortalPosition({ top, left });
      setPosition(newPosition);
    };

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible && isMountedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        // Unregister from manager when hiding
        tooltipManager.unregister(dismissTooltip);
        setIsVisible(false);
        // Return focus to trigger if it's focusable
        // Use requestAnimationFrame to ensure focus happens after state update
        requestAnimationFrame(() => {
          if (triggerRef.current && 'focus' in triggerRef.current && isMountedRef.current) {
            (triggerRef.current as HTMLElement).focus();
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isVisible, calculatePosition, dismissTooltip, variant, preferLeftAlignWhenNearEdge]);

  // Helper function to capitalize the first letter of a string
  const capitalizeFirstLetter = (text: string): string => {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Helper function to render text that can be a string or array of strings (multiple paragraphs)
  const renderParagraphs = (content: string | string[] | undefined): React.ReactNode => {
    if (!content) return null;
    
    if (Array.isArray(content)) {
      return content.map((paragraph, index) => (
        <p key={index} className={index > 0 ? 'mt-2' : ''}>
          {capitalizeFirstLetter(paragraph)}
        </p>
      ));
    }
    
    return <>{capitalizeFirstLetter(content)}</>;
  };

  // Determine if we have structured content or legacy text
  const hasStructuredContent = !!content;
  const displayText = text || (Array.isArray(content?.description) 
    ? content.description.join(' ') 
    : content?.description || '');

  // Get arrow classes for Tailwind (matching Figma implementation). Arrow position matches tooltip alignment.
  const getArrowClasses = (): string => {
    const leftAligned = useLeftAlign && (position === 'top' || position === 'bottom');
    switch (position) {
      case 'top':
        return leftAligned ? 'top-full left-4 -translate-x-1/2 -translate-y-1/2 rotate-45' : 'top-full left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45';
      case 'bottom':
        return leftAligned ? 'bottom-full left-4 -translate-x-1/2 translate-y-1/2 rotate-45' : 'bottom-full left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45';
      case 'left':
        return 'left-full top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45';
      case 'right':
        return 'right-full top-1/2 translate-x-1/2 -translate-y-1/2 rotate-45';
      default:
        return leftAligned ? 'top-full left-4 -translate-x-1/2 -translate-y-1/2 rotate-45' : 'top-full left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45';
    }
  };

  const isLightVariant = variant === 'light';
  const tooltipWidthClasses = fitContent
    ? 'w-max min-w-0 max-w-tooltip'
    : 'min-w-tooltip max-w-tooltip';
  const tooltipContentClasses = isLightVariant
    ? `bg-white dark:bg-neutral-100 text-neutral-900 dark:text-neutral-900 rounded-lg p-3 shadow-tooltip ${tooltipWidthClasses} border border-neutral-200 dark:border-neutral-300`
    : `bg-neutral-900 text-white rounded-lg p-3 shadow-tooltip ${tooltipWidthClasses}`;
  const tooltipArrowClasses = isLightVariant ? 'bg-white dark:bg-neutral-100 border-neutral-200 dark:border-neutral-300' : 'bg-neutral-900';

  // Z-index above form sections (10050) and datepicker so tooltips always appear on top
  const TOOLTIP_PORTAL_Z_INDEX = 10300;

  // Shared tooltip inner content (card + arrow) for both inline and portaled render
  const tooltipPopoverContent = (
    <>
      <div
        className={tooltipContentClasses}
        style={{ lineHeight: '1.4' }}
      >
        {hasStructuredContent ? (
          <>
            {content.title && (
              <div className={`font-semibold text-tooltip-title mb-2 ${isLightVariant ? 'text-neutral-900' : 'text-white'}`}>
                {content.title}
              </div>
            )}
            <div className={`text-tooltip-body mb-2 ${isLightVariant ? 'text-neutral-700' : 'text-neutral-100'}`}>
              {renderParagraphs(content.description)}
            </div>
            {content.guidance && (
              <div className={`text-tooltip-body mb-2 ${isLightVariant ? 'text-neutral-700' : 'text-neutral-100'}`}>
                <span className="font-medium">Guidance:</span> {renderParagraphs(content.guidance)}
              </div>
            )}
            {content.example && (
              <div className={`text-tooltip-body mb-2 italic ${isLightVariant ? 'text-neutral-600' : 'text-neutral-300'}`}>
                <span className="font-medium not-italic">Example:</span> {renderParagraphs(content.example)}
              </div>
            )}
            {content.notes && (
              <div className={`text-tooltip-body mt-2 pt-2 border-t ${isLightVariant ? 'text-neutral-600 border-neutral-200' : 'text-neutral-300 border-neutral-700'}`}>
                <span className="font-medium">Note:</span> {renderParagraphs(content.notes)}
              </div>
            )}
          </>
        ) : (
          <div className={`text-tooltip-body ${isLightVariant ? 'text-neutral-700' : 'text-neutral-100'}`}>{displayText}</div>
        )}
      </div>
      <div className={`absolute w-tooltip-arrow h-tooltip-arrow ${tooltipArrowClasses} ${isPositioned ? getArrowClasses() : ''}`} />
    </>
  );

  // Portal tooltip to document.body so it draws above all form elements (sections, datepicker, sidebar) — Form Engine pattern
  const portaledTooltip =
    isVisible ? (
      <div
        ref={tooltipRef}
        id={tooltipId}
        className="relative"
        style={{
          position: 'fixed',
          left: portalPosition?.left ?? 0,
          top: portalPosition?.top ?? 0,
          zIndex: TOOLTIP_PORTAL_Z_INDEX,
          opacity: portalPosition ? 1 : 0,
          pointerEvents: portalPosition ? 'auto' : 'none',
          transition: 'opacity 0.1s ease-out',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="tooltip"
        aria-live="polite"
      >
        {tooltipPopoverContent}
      </div>
    ) : null;

  // If children are provided, wrap them with tooltip functionality
  if (children) {
    return (
      <>
        <span
          className={`tooltip-wrapper-inline ${className}`}
          ref={triggerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          tabIndex={focusableWrapper ? 0 : -1}
          aria-describedby={isVisible ? tooltipId : undefined}
        >
          {children}
        </span>
        {isVisible && createPortal(portaledTooltip, document.body)}
      </>
    );
  }

  // If no children, return null (no icon-based tooltip)
  return null;
};
